import React, { useState, useEffect } from 'react';

// Context Imports
// REMOVED: useAuth and useAdminTracker are no longer needed for tracking
import { useBusinessEdits } from '../../authentication_components/businessEditContext';

// Icon Imports
import { 
  Settings, 
  Key, 
  Save, 
  FileText, 
  XCircle, 
  CheckCircle, 
  AlertTriangle, 
  Loader2,
  Eye,
  EyeOff
} from 'lucide-react';

// =================================================================
// BusinessEditDialog Component (Master Only)
// =================================================================
const BusinessEditDialog = ({ isOpen, onClose, business, onUpdateSuccess }) => {
  // Only use the business edits context now
  const { updateBusinessProfile, updateBusinessPassword, loading, error, clearState } = useBusinessEdits();
  
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'security'
  const [successMsg, setSuccessMsg] = useState('');
  
  // Password visibility state
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Profile Form State
  const [formData, setFormData] = useState({
    business_name: '',
    business_year: '',
    business_category: '',
    business_owner: '',
    business_email: '',
    business_phone: ''
  });

  // Password Form State
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Initialize data when dialog opens
  useEffect(() => {
    if (isOpen && business) {
      setFormData({
        business_name: business.business_name || '',
        business_year: business.business_year || new Date().getFullYear(),
        business_category: business.business_category || '',
        business_owner: business.business_owner || '',
        business_email: business.business_email || '',
        business_phone: business.business_phone || ''
      });
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setSuccessMsg('');
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      clearState();
    }
  }, [isOpen, business, clearState]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    
    try {
      await updateBusinessProfile(business.business_registry_number, formData);
      
      // LOGIC REMOVED: Admin Tracker creation
      
      setSuccessMsg('Perfil actualizado correctamente');
      
      if (onUpdateSuccess) onUpdateSuccess();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error("Profile update failed", err);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("Las contraseñas no coinciden");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    try {
      await updateBusinessPassword(business.business_registry_number, passwordData.newPassword);
      
      // LOGIC REMOVED: Admin Tracker creation

      setSuccessMsg('Contraseña actualizada correctamente');
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setShowNewPassword(false);
      setShowConfirmPassword(false);

    } catch (err) {
      console.error("Password update failed", err);
    }
  };

  if (!isOpen || !business) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-[80] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <div>
             <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Settings className="h-5 w-5 text-purple-600" />
              Edición Maestra
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Editando: {business.business_name} ({business.business_registry_number})
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'profile' 
                ? 'border-b-2 border-purple-500 text-purple-600 bg-purple-50/50' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FileText className="h-4 w-4" /> Información General
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'security' 
                ? 'border-b-2 border-red-500 text-red-600 bg-red-50/50' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Key className="h-4 w-4" /> Seguridad / Contraseña
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto">
          {successMsg && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700 text-sm animate-fade-in">
              <CheckCircle className="h-4 w-4 mr-2" /> {successMsg}
            </div>
          )}
          
          {error && (
             <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700 text-sm animate-fade-in">
              <AlertTriangle className="h-4 w-4 mr-2" /> {error}
            </div>
          )}

          {activeTab === 'profile' ? (
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Nombre del Negocio</label>
                  <input 
                    type="text" 
                    value={formData.business_name} 
                    onChange={e => setFormData({...formData, business_name: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Dueño</label>
                  <input 
                    type="text" 
                    value={formData.business_owner} 
                    onChange={e => setFormData({...formData, business_owner: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                  <input 
                    type="email" 
                    value={formData.business_email} 
                    onChange={e => setFormData({...formData, business_email: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Teléfono</label>
                  <input 
                    type="text" 
                    value={formData.business_phone} 
                    onChange={e => setFormData({...formData, business_phone: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Categoría</label>
                  <input 
                    type="text" 
                    value={formData.business_category} 
                    onChange={e => setFormData({...formData, business_category: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Año de Fundación</label>
                  <input 
                    type="number" 
                    value={formData.business_year} 
                    onChange={e => setFormData({...formData, business_year: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>
              </div>

              <div className="pt-4 flex justify-end">
                <button 
                  type="submit" 
                  disabled={loading}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition flex items-center gap-2 font-medium shadow-sm disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Guardar Cambios
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg text-sm text-orange-800 mb-4">
                <p className="font-bold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Advertencia
                </p>
                <p className="mt-1">
                  Cambiar la contraseña cerrará la sesión del usuario en todos sus dispositivos. 
                  Asegúrese de notificar al dueño del negocio sobre la nueva credencial.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nueva Contraseña</label>
                <div className="relative">
                  <input 
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword} 
                    onChange={e => setPasswordData({...passwordData, newPassword: e.target.value})}
                    className="w-full p-3 pr-12 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none"
                    placeholder="Mínimo 6 caracteres"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Confirmar Contraseña</label>
                <div className="relative">
                  <input 
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword} 
                    onChange={e => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    className="w-full p-3 pr-12 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 outline-none"
                    placeholder="Repita la contraseña"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="pt-4 flex justify-end">
                <button 
                  type="submit" 
                  disabled={loading}
                  className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition flex items-center gap-2 font-medium shadow-sm disabled:opacity-50"
                >
                   {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                  Actualizar Contraseña
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessEditDialog;